import {
  RiAddLine,
  RiAlertLine,
  RiArrowLeftLine,
  RiArrowRightLine,
  RiCheckLine,
  RiDeleteBinLine,
  RiEditLine,
  RiFileCopyLine,
  RiPauseLine,
} from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import type { JSX, MouseEvent } from 'react';
import type { BrandOffer, OfferType } from '@/types/BrandOffer.types';
import {
  OFFER_TYPE_COLORS,
  OFFER_TYPE_LABELS,
} from '../BrandOffersPage.constants';
import type { OffersTableProps } from '../BrandOffersPage.types';
import {
  OFFERS_TABLE_COPY,
  selectOfferAriaLabel,
} from './OffersTable.constants';
import styles from './OffersTable.module.css';
import {
  brandOfferDetailPath,
  discountSummary,
  formatBulkSelectionCount,
  formatEndDate,
  formatPageIndicator,
  formatPaginationSummary,
} from './OffersTable.utils';

interface OfferRowMeta {
  typeColor: string;
  discount: string | null;
  isExpired: boolean;
}

function getOfferRowMeta(offer: BrandOffer): OfferRowMeta {
  const typeColor = OFFER_TYPE_COLORS[offer.offerType as OfferType];
  const discount = discountSummary(offer.discountConfig, offer.offerType);
  const isExpired = offer.endsAt ? new Date(offer.endsAt) < new Date() : false;
  return { typeColor, discount, isExpired };
}

interface OfferRowPartsProps {
  offer: BrandOffer;
  brandMap: Map<number, string>;
  togglingId: number | null;
  onToggleActive: (offer: BrandOffer) => Promise<void>;
  onEdit: (offer: BrandOffer) => void;
  onDuplicate: (offer: BrandOffer) => void;
  onDeleteRequest: (id: number) => void;
}

function OfferBrandBlock({
  offer,
  brandMap,
}: Pick<OfferRowPartsProps, 'offer' | 'brandMap'>): JSX.Element {
  if (offer.brand === null) {
    return (
      <>
        <span className={styles.brandCellTitleMuted}>—</span>
        <span className={styles.scopeHint}>
          {OFFERS_TABLE_COPY.globalScope}
        </span>
      </>
    );
  }
  return (
    <>
      <span className={styles.brandCellTitle}>
        {brandMap.get(offer.brand) ?? `#${offer.brand}`}
      </span>
      <span className={styles.scopeHint}>
        {OFFERS_TABLE_COPY.singleBrandScope}
      </span>
    </>
  );
}

function OfferStatusBlock({
  offer,
  isExpired,
  togglingId,
  onToggleActive,
}: Pick<OfferRowPartsProps, 'offer' | 'togglingId' | 'onToggleActive'> & {
  isExpired: boolean;
}): JSX.Element {
  return (
    <div className={styles.statusCellInner}>
      <button
        type="button"
        className={
          offer.isActive ? `${styles.toggle} ${styles.toggleOn}` : styles.toggle
        }
        disabled={togglingId === offer.id}
        title={
          offer.isActive
            ? OFFERS_TABLE_COPY.togglePauseTitle
            : OFFERS_TABLE_COPY.toggleActivateTitle
        }
        onClick={() => void onToggleActive(offer)}
      >
        <span className={styles.toggleThumb} />
      </button>
      <div className={styles.statusMeta}>
        <span
          className={offer.isActive ? styles.liveLabel : styles.pausedLabel}
        >
          {offer.isActive
            ? OFFERS_TABLE_COPY.statusLive
            : OFFERS_TABLE_COPY.statusPaused}
        </span>
        {offer.endsAt && (
          <span className={isExpired ? styles.expiredDate : styles.dateHint}>
            {isExpired
              ? OFFERS_TABLE_COPY.dateEndedPrefix
              : OFFERS_TABLE_COPY.dateEndsPrefix}
            {formatEndDate(offer.endsAt)}
          </span>
        )}
      </div>
    </div>
  );
}

function OfferActionButtons({
  offer,
  onEdit,
  onDuplicate,
  onDeleteRequest,
}: Pick<
  OfferRowPartsProps,
  'offer' | 'onEdit' | 'onDuplicate' | 'onDeleteRequest'
>): JSX.Element {
  return (
    <>
      <button
        type="button"
        className={styles.actionBtn}
        title={OFFERS_TABLE_COPY.actionEditTitle}
        onClick={() => onEdit(offer)}
      >
        <RiEditLine size={16} />
      </button>
      <button
        type="button"
        className={styles.actionBtn}
        title={OFFERS_TABLE_COPY.actionDuplicateTitle}
        onClick={() => onDuplicate(offer)}
      >
        <RiFileCopyLine size={16} />
      </button>
      <button
        type="button"
        className={`${styles.actionBtn} ${styles.actionDanger}`}
        title={OFFERS_TABLE_COPY.actionRemoveTitle}
        onClick={() => onDeleteRequest(offer.id)}
      >
        <RiDeleteBinLine size={16} />
      </button>
    </>
  );
}

const OffersTable = ({
  offers,
  isLoading,
  error,
  totalCount,
  page,
  totalPages,
  pageSize,
  hasPrevious,
  hasNext,
  brandMap,
  selectedIds,
  allSelected,
  togglingId,
  isBulkActing,
  conflictingBrands,
  onSelectAll,
  onToggleSelect,
  onToggleActive,
  onEdit,
  onDuplicate,
  onDeleteRequest,
  onPageChange,
  onOpenNew,
  onBulkActivate,
  onBulkDeactivate,
  onBulkDelete,
}: OffersTableProps): JSX.Element => {
  const navigate = useNavigate();
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalCount);
  const canGoPrevious = !isLoading && page > 1 && hasPrevious;
  const canGoNext = !isLoading && page < totalPages && hasNext;

  const stopPropagation = (event: MouseEvent): void => {
    event.stopPropagation();
  };

  return (
    <div className={styles.mainPanel}>
      {conflictingBrands.length > 0 && (
        <div className={styles.conflictBanner}>
          <RiAlertLine size={18} />
          <div>
            <p className={styles.conflictTitle}>
              {OFFERS_TABLE_COPY.conflictTitle}
            </p>
            <p className={styles.conflictBody}>
              {OFFERS_TABLE_COPY.conflictBeforeBrands}{' '}
              <strong>{conflictingBrands.join(', ')}</strong>
              {OFFERS_TABLE_COPY.conflictAfterBrands}
            </p>
          </div>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className={styles.bulkBar}>
          <span className={styles.bulkCount}>
            {formatBulkSelectionCount(selectedIds.size)}
          </span>
          <div className={styles.bulkActions}>
            <button
              type="button"
              className={styles.bulkActivate}
              disabled={isBulkActing}
              onClick={onBulkActivate}
            >
              <RiCheckLine size={14} /> {OFFERS_TABLE_COPY.bulkTurnOn}
            </button>
            <button
              type="button"
              className={styles.bulkDeactivate}
              disabled={isBulkActing}
              onClick={onBulkDeactivate}
            >
              <RiPauseLine size={14} /> {OFFERS_TABLE_COPY.bulkPause}
            </button>
            <button
              type="button"
              className={styles.bulkDelete}
              disabled={isBulkActing}
              onClick={onBulkDelete}
            >
              <RiDeleteBinLine size={14} /> {OFFERS_TABLE_COPY.bulkRemove}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className={styles.errorBanner}>
          <p>{error}</p>
        </div>
      )}

      <div className={styles.tableCard}>
        {isLoading ? (
          <div className={styles.tableState}>
            <div className={styles.spinner} />
            <p className={styles.tableStateTitle}>
              {OFFERS_TABLE_COPY.loadingTitle}
            </p>
            <p className={styles.tableStateHint}>
              {OFFERS_TABLE_COPY.loadingHint}
            </p>
          </div>
        ) : offers.length === 0 ? (
          <div className={styles.tableState}>
            <p className={styles.tableStateTitle}>
              {OFFERS_TABLE_COPY.emptyTitle}
            </p>
            <p className={styles.tableStateHint}>
              {OFFERS_TABLE_COPY.emptyHint}
            </p>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={onOpenNew}
            >
              <RiAddLine size={16} /> {OFFERS_TABLE_COPY.emptyCta}
            </button>
          </div>
        ) : (
          <>
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.thCheck}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        aria-label={OFFERS_TABLE_COPY.selectAllAria}
                        onChange={onSelectAll}
                      />
                    </th>
                    <th className={styles.th}>{OFFERS_TABLE_COPY.thIdType}</th>
                    <th className={styles.th}>{OFFERS_TABLE_COPY.thOffer}</th>
                    <th className={styles.th}>
                      {OFFERS_TABLE_COPY.thBrandScope}
                    </th>
                    <th className={`${styles.th} ${styles.center}`}>
                      {OFFERS_TABLE_COPY.thRank}
                    </th>
                    <th className={styles.th}>{OFFERS_TABLE_COPY.thStatus}</th>
                    <th className={`${styles.th} ${styles.right}`}>
                      {OFFERS_TABLE_COPY.thActions}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {offers.map((offer) => {
                    const { typeColor, discount, isExpired } =
                      getOfferRowMeta(offer);

                    return (
                      <tr
                        key={offer.id}
                        className={
                          selectedIds.has(offer.id)
                            ? `${styles.row} ${styles.rowSelected} ${styles.rowClickable}`
                            : `${styles.row} ${styles.rowClickable}`
                        }
                        onClick={() => navigate(brandOfferDetailPath(offer.id))}
                      >
                        <td
                          className={styles.tdCheck}
                          onClick={stopPropagation}
                        >
                          <input
                            type="checkbox"
                            checked={selectedIds.has(offer.id)}
                            aria-label={selectOfferAriaLabel(offer.id)}
                            onChange={() => onToggleSelect(offer.id)}
                          />
                        </td>

                        <td className={styles.tdIdType}>
                          <span className={styles.tdIdNum}>#{offer.id}</span>
                          <span
                            className={styles.typeBadge}
                            style={{
                              backgroundColor: typeColor + '22',
                              color: typeColor,
                            }}
                          >
                            {OFFER_TYPE_LABELS[offer.offerType as OfferType]}
                          </span>
                        </td>

                        <td className={styles.tdOffer}>
                          <span className={styles.offerName}>
                            {offer.name || (
                              <span className={styles.unnamed}>
                                {OFFERS_TABLE_COPY.unnamedOffer}
                              </span>
                            )}
                          </span>
                          {discount && (
                            <span className={styles.discountHint}>
                              {discount}
                            </span>
                          )}
                        </td>

                        <td className={styles.tdBrand}>
                          <OfferBrandBlock offer={offer} brandMap={brandMap} />
                        </td>

                        <td className={`${styles.td} ${styles.center}`}>
                          <span className={styles.rankPill}>
                            {offer.priority}
                          </span>
                        </td>

                        <td
                          className={styles.tdStatus}
                          onClick={stopPropagation}
                        >
                          <OfferStatusBlock
                            offer={offer}
                            isExpired={isExpired}
                            togglingId={togglingId}
                            onToggleActive={onToggleActive}
                          />
                        </td>

                        <td
                          className={`${styles.td} ${styles.right}`}
                          onClick={stopPropagation}
                        >
                          <OfferActionButtons
                            offer={offer}
                            onEdit={onEdit}
                            onDuplicate={onDuplicate}
                            onDeleteRequest={onDeleteRequest}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className={styles.offerCardList}>
              {offers.map((offer) => {
                const { typeColor, discount, isExpired } =
                  getOfferRowMeta(offer);
                const isSelected = selectedIds.has(offer.id);

                return (
                  <article
                    key={offer.id}
                    className={
                      isSelected
                        ? `${styles.offerCard} ${styles.offerCardSelected}`
                        : styles.offerCard
                    }
                    onClick={() => navigate(brandOfferDetailPath(offer.id))}
                  >
                    <div
                      className={styles.offerCardTop}
                      onClick={stopPropagation}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        aria-label={selectOfferAriaLabel(offer.id)}
                        onChange={() => onToggleSelect(offer.id)}
                      />
                      <div className={styles.offerCardMeta}>
                        <span className={styles.tdIdNum}>#{offer.id}</span>
                        <span
                          className={styles.typeBadge}
                          style={{
                            backgroundColor: typeColor + '22',
                            color: typeColor,
                          }}
                        >
                          {OFFER_TYPE_LABELS[offer.offerType as OfferType]}
                        </span>
                        <span className={styles.rankPill}>
                          {offer.priority}
                        </span>
                      </div>
                    </div>

                    <div className={styles.offerCardBody}>
                      <p className={styles.offerCardTitle}>
                        {offer.name || (
                          <span className={styles.unnamed}>
                            {OFFERS_TABLE_COPY.unnamedOffer}
                          </span>
                        )}
                      </p>
                      {discount && (
                        <p className={styles.offerCardDiscount}>{discount}</p>
                      )}
                      <div className={styles.offerCardBrand}>
                        <OfferBrandBlock offer={offer} brandMap={brandMap} />
                      </div>
                    </div>

                    <div
                      className={styles.offerCardFooter}
                      onClick={stopPropagation}
                    >
                      <OfferStatusBlock
                        offer={offer}
                        isExpired={isExpired}
                        togglingId={togglingId}
                        onToggleActive={onToggleActive}
                      />
                      <div className={styles.offerCardActions}>
                        <OfferActionButtons
                          offer={offer}
                          onEdit={onEdit}
                          onDuplicate={onDuplicate}
                          onDeleteRequest={onDeleteRequest}
                        />
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>

      {!isLoading && totalCount > 0 && (
        <div className={styles.pagination}>
          <span className={styles.paginationInfo}>
            {formatPaginationSummary(rangeStart, rangeEnd, totalCount)}
          </span>
          <div className={styles.paginationBtns}>
            <button
              type="button"
              className={styles.pageBtn}
              disabled={!canGoPrevious}
              onClick={() => onPageChange(page - 1)}
            >
              <RiArrowLeftLine aria-hidden size={16} />
              {OFFERS_TABLE_COPY.paginationPrevious}
            </button>
            <span className={styles.pageNum}>
              {formatPageIndicator(page, totalPages)}
            </span>
            <button
              type="button"
              className={styles.pageBtn}
              disabled={!canGoNext}
              onClick={() => onPageChange(page + 1)}
            >
              {OFFERS_TABLE_COPY.paginationNext}
              <RiArrowRightLine aria-hidden size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OffersTable;
